import Map from 'ol/Map';
import View from 'ol/View';
import { ScaleLine, Zoom, ZoomToExtent } from 'ol/control';
import { eventBus } from './eventBus.js';
//import { projection } from './projections/epgsg-21781.js';
import { projection } from './projections/epgsg-3857.js';
import { label } from './layers/label.js';
import { water } from './layers/water.js';
import { relief } from './layers/relief.js';
import { region } from './layers/region.js';
import { popup } from './layers/popup.js';
import { copyright } from './gui/copyright.js';
import { circle } from './layers/circle.js';
import { svg } from './utils/svg.js';
import { byId } from './utils/dom.js';
import { config } from 'config';
import { buffer } from 'ol/extent.js';

/**
 * @module map
 */
export const map = {

  /**
   * @type {JsonStatReader}
   */
  _reader: null,

  /**
   * ID of the map div
   * @type {String}
   * @private
   */
  _mapDivId: 'map',

  /**
   * DOM element of the map div
   * @type {HTMLDivElement}
   * @private
   */
  _mapDivElement: null,

  /**
   * Zoom configuration of the map
   * @type {ZoomConfiguration}
   * @private
   */
  _zoom: {
    min: 5,
    max: 12
  },

  /**
   * How much should the map zoom on a zoom button click?
   * @type {Number}
   * @private
   */
  _zoomButtonStep: 0.5,

  /**
   * OpenLayers View instance
   * @type {View}
   * @private
   */
  _view: null,

  /**
   * OpenLayers Map instance
   * @type {Map}
   */
  map: null,

  /**
   * @type {module:layers/relief}
   */
  relief: null,

  /**
   * @type {module:layers/water}
   */
  water: null,

  /**
   * @type {module:layers/region}
   */
  region: null,

  /**
   * @type {module:layers/label}
   */
  label: null,

  /**
   * @ype {module:popup}
   */
  popup: null,

  /**
   * @tpye {module:copyright}
   */
  copyright: null,

  /**
   * Flag if the mouse is over any of the regions.
   * @type {Boolean}
   */
  _regionHovered: false,

  /**
   * Initialize the map.
   * @param {JsonStatReader} jsonStatReader
   * @param {DataSeries} rowData
   * @returns {map}
   */
  async init (jsonStatReader, rowData) {
    this._reader = jsonStatReader;
    this._mapDivElement = byId(this._mapDivId);

    this.relief = relief.init();
    this.water = water.init();
    this.region = await region.init(this._reader, rowData);
    this.popup = popup.init(this._reader, rowData);
    this.copyright = copyright.init(this._reader);
    this.copyright.render();

    const layers = [
      relief.layer,
      region.layer,
      water.layer,
      label.layer // layer is initialized (label points loaded) in the map application to fetch in parallel
    ];
    const viewOptions = {
      //projection: projection.get(), // Explicitly set the map projection
      center: projection.center,
      minZoom: this._zoom.min,
      maxZoom: this._zoom.max
    };
    this._view = new View(viewOptions);

    const varNr = jsonStatReader.getRegionId();
    const extent = await this.region.fetchExtent(varNr);
    this._bufferedExtent = buffer(extent, relief.BUFFER_SIZE);

    // Store the initial extent immediately
    this._view.set('initialExtent', this._bufferedExtent);

    this.map = new Map({
      target: this._mapDivElement,
      layers: layers,
      view: this._view,
      controls: [
        new Zoom(),
        new ZoomToExtent({
          label: this._createButtonExtent(),
          extent: this._bufferedExtent
        }),
        new ScaleLine(),
        this.copyright.control
      ],
      overlays: [this.popup.overlay]
    });
    // set the extent here and not as property of the viewOption, since that would constrain the map to that extent
    this.centerView();
    this._initEvents();
  },

  /**
   * Initialize all event listeners.
   * @listens HTMLInputElement~event:change
   * @listens Map~event:rendercomplete
   * @listens Map~event:singleclick
   * @listens Map~event:pointermove
   * @returns {void}
   * @private
   */
  _initEvents () {
    this.map.once('rendercomplete', () => {
      this.map.on('singleclick', this._handleClick.bind(this));
      this.map.on('pointermove', this._handlePointerMove.bind(this));
    });
  },

  /**
   * Update the map.
   * @param {DataSeries} rowData
   */
  update (rowData) {
    region.update(rowData);
    circle.update(rowData);
    label.update(rowData);
    popup.update(rowData);
  },

  /**
   * Center the map view and zoom out.
   * @param {function} [fnc] Function called when the view is in its final position.
   * @returns {void}
   */
  centerView (fnc) {
    let options = {};
    if (fnc) {
      options.callback = fnc;
    }
    this._view.setCenter(projection.center);
    this._view.fit(this._bufferedExtent, options);
    this._view.set('initialZoom', this._view.getZoom());  // remember the initial zoom level by setting custom property
    this._view.set('initialResolution', this._view.getResolution());
  },

  /**
   * Create the center view symbol for the customized control.
   * @return {SVGElement}
   * @private
   */
  _createButtonExtent () {
    let el = svg.create('svg');
    el.innerHTML = '<use xlink:href="' + config.source.img + 'toolbar.svg#center"></use>';

    return el;
  },

  /**
   * Handle the pointer move event and dispatch custom leave and enter events.
   * @emits eventBus~regionEnter
   * @emits eventBus~regionLeave
   * @param {MapBrowserEvent} evt
   * @private
   */
  _handlePointerMove (evt) {
    if (this._regionHovered) {
      region.updateStyleBorder(evt);
      eventBus.dispatch('regionLeave');
      this._regionHovered = false;
    }
    this.map.forEachFeatureAtPixel(evt.pixel, feature => {
        region.updateStyleBorder(evt, feature);
        eventBus.dispatch('regionEnter', {
          regionId: feature.getId()
        });
        this._regionHovered = true;
      },
      {
        layerFilter: layer => layer === region.layer
      });
  },

  /**
   * Handle the click event and shows the popup.
   * @param {MapBrowserEvent} evt
   * @private
   */
  _handleClick (evt) {
    this.map.forEachFeatureAtPixel(evt.pixel, feature => {
      popup.show(evt, feature);
    });
  }
};